import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { createAccountInvite } from 'api/account';
import { CampaignSelect } from 'assets/js/pages/account-settings/users/CampaignSelect';
import { UserRoleFields } from 'assets/js/pages/account-settings/users/UserRoleFields';
import { RoleSettings } from 'assets/js/pages/account-settings/users/types';
import {
    campaignSelectionToRequestValue,
    getRoleIdsForSubmission,
} from 'assets/js/pages/account-settings/users/utils';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useAccountMemberRoles } from 'customHooks/useAccountMemberRoles';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AccountMemberRoleInfo } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface InviteUserSettingsFormValues extends RoleSettings {
    firstName: string;
    lastName: string;
    email: string;
}

const InviteUserDrawer = () => {
    const { invitesCampaignFiltering } = useFlags();
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { goBack } = useDialogRoute();
    const { data: roles } = useAccountMemberRoles();

    const methods = Form.useGivebutterForm<InviteUserSettingsFormValues>({
        defaultValues: toForm(roles.data),
    });

    const { reset, handleSubmit, control, path, watch } = methods;

    const campaigns = watch('campaigns');
    const hasCampaignsSelected = campaigns.length > 0;

    const { isPending: isSubmitting, mutate: submit } = useMutation({
        mutationFn: (inviteFormData: InviteUserSettingsFormValues) =>
            createAccountInvite(accountId, toRequest(inviteFormData, invitesCampaignFiltering)),
        onSuccess: () => {
            toast.success('Invited account member successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pendingUsers(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.membersWithFilters(accountId),
            });
            reset();
            goBack();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to invite account member');
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<InviteUserSettingsFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Invite users</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col gap-4">
                            <div className="flex gap-4">
                                <Form.Container>
                                    <Form.Label name={path('firstName')}>
                                        First name
                                        <Form.LabelAsterisk />
                                    </Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={'firstName'}
                                        rules={{
                                            required: 'First name is required.',
                                        }}
                                    >
                                        <Form.TextInputForm placeholder={'First name'} />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('firstName')} />
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label name={path('lastName')}>
                                        Last name
                                        <Form.LabelAsterisk />
                                    </Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={'lastName'}
                                        rules={{
                                            required: 'Last name is required.',
                                        }}
                                    >
                                        <Form.TextInputForm placeholder={'Last name'} />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('lastName')} />
                                </Form.Container>
                            </div>
                            <Form.Container>
                                <Form.Label name={path(`email`)}>
                                    Email
                                    <Form.LabelAsterisk />
                                </Form.Label>
                                <Form.TextInputRoot
                                    control={control}
                                    name={`email`}
                                    rules={{ required: 'Email is required.' }}
                                >
                                    <Form.TextInputForm placeholder={'Email'} />
                                </Form.TextInputRoot>
                                <Form.Error name={path(`email`)} />
                            </Form.Container>
                        </div>
                        <UserRoleFields />
                        {invitesCampaignFiltering && (
                            <>
                                <CampaignSelect />
                                {hasCampaignsSelected && (
                                    <Form.Container>
                                        <div className={'flex items-center gap-2'}>
                                            <Form.Checkbox name={path('addToFutureCampaigns')} />
                                            <Form.Label name={path('addToFutureCampaigns')}>
                                                Allow access to future campaigns
                                            </Form.Label>
                                        </div>
                                        <Form.Error name={path('addToFutureCampaigns')} />
                                    </Form.Container>
                                )}
                            </>
                        )}
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel>Cancel</Drawer.Cancel>
                        <Form.SubmitButton disabled={isSubmitting}>Send invite</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const toForm = (roles: AccountMemberRoleInfo[]) => ({
    firstName: '',
    lastName: '',
    email: '',
    roles: roles.map(role => ({
        id: role.id,
        name: role.name,
        description: role.description,
        checked: false,
        userCanAssign: role.user_can_assign,
    })),
    campaigns: [],
    addToFutureCampaigns: null,
});

const toRequest = (form: InviteUserSettingsFormValues, invitesCampaignFiltering: boolean) => {
    const campaigns = campaignSelectionToRequestValue(form.campaigns);

    return {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        roles: getRoleIdsForSubmission(form.roles),
        ...(invitesCampaignFiltering
            ? {
                  campaign_ids: campaigns,
                  add_to_future_campaigns:
                      campaigns === null ? undefined : Boolean(form.addToFutureCampaigns),
              }
            : {}),
    };
};

export { InviteUserDrawer };
